import { memo, useRef } from "react";
import { money } from "../lib/api";
import { Plus, Prohibit } from "@phosphor-icons/react";

/**
 * POS product card — the clickable tile on the Checkout page.
 *
 * Design goals:
 *   • Instant visual variety — cards differ based on category + kind, so
 *     staff can scan the grid at a glance instead of reading every label.
 *   • Clear stock state — green / amber / red dot with bar graph so low or
 *     out-of-stock items are obvious even from 5 ft away.
 *   • Touch-friendly — whole card is a tap target, hover reveals a "+" FAB
 *     in the top-right for mouse users.
 *   • Accessible — out-of-stock cards are visually dimmed and truly disabled
 *     (the onClick short-circuits upstream in POS.jsx).
 *
 * Category colours are hashed deterministically from the category string so
 * "Screen Protectors" is always the same teal across every session without
 * any DB changes.
 */

// Curated palette — picked to be distinct under fluorescent shop lighting.
const CATEGORY_PALETTE = [
  { bg: "#EFF6FF", fg: "#1E5FEB", spine: "#3B82F6" },   // blue
  { bg: "#F0FDF4", fg: "#0F9D58", spine: "#10B981" },   // green
  { bg: "#FEF3C7", fg: "#92400E", spine: "#F59E0B" },   // amber
  { bg: "#FCE7F3", fg: "#BE185D", spine: "#EC4899" },   // pink
  { bg: "#EDE9FE", fg: "#6D28D9", spine: "#8B5CF6" },   // purple
  { bg: "#CFFAFE", fg: "#0E7490", spine: "#06B6D4" },   // cyan
  { bg: "#FFE4E6", fg: "#BE123C", spine: "#F43F5E" },   // rose
  { bg: "#F5F5F4", fg: "#57534E", spine: "#78716C" },   // stone (fallback)
];

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h);
}

function colourFor(category) {
  const key = (category || "").trim().toLowerCase();
  if (!key) return CATEGORY_PALETTE[CATEGORY_PALETTE.length - 1];
  return CATEGORY_PALETTE[hashString(key) % (CATEGORY_PALETTE.length - 1)];
}

const KIND_LABELS = {
  part: "PART",
  accessory: "ACC",
  other: "",
};


function ProductCardInner({ product, onAdd }) {
  const palette = colourFor(product.category);
  const isOut = !product.stock || product.stock <= 0;
  const kindLabel = KIND_LABELS[product.kind] || "";

  // Ghost-click guard for touchscreen tills. iOS/Android browsers can
  // emit BOTH a `touchend` and a synthetic `click` for a single tap on
  // older drivers / cheap capacitive screens — without this, one tap
  // adds the same product to the cart twice. 250ms is well below a real
  // user's "I want two of these" double-tap (~400ms+) but above any
  // single-tap ghost-fire (~50-100ms). Per-card ref so fast scans of
  // *different* SKUs aren't blocked.
  const lastClickRef = useRef(0);
  const handleClick = () => {
    if (isOut) return;
    const now = Date.now();
    if (now - lastClickRef.current < 250) return;
    lastClickRef.current = now;
    onAdd(product);
  };

  // Continuous red → amber → green ramp based on how "full" the shelf is.
  // `fullStock` is the mark at which the bar is 100% green & solid; we use
  // 4× the low-stock threshold (default 5 → full at 20 units) so busy SKUs
  // spend most of their life in the green zone.
  const threshold = product.low_stock_threshold || 5;
  const fullStock = Math.max(threshold * 4, 20);
  const ratio = Math.min(1, Math.max(0, product.stock / fullStock));
  const hue = Math.round(ratio * 120);               // 0° red → 120° green
  const barColour = isOut ? "#E5E7EB" : `hsl(${hue}, 70%, 72%)`;
  const labelColour = isOut ? "#DC2626" : `hsl(${hue}, 55%, 36%)`;
  // Bar width follows the same ratio so cashier *sees* the shelf emptying —
  // clamp to a min so the track doesn't vanish entirely near zero.
  const barPct = isOut ? 0 : Math.max(8, Math.round(ratio * 100));

  return (
    <button
      type="button"
      data-testid={`pos-product-${product.sku}`}
      onClick={handleClick}
      disabled={isOut}
      className={`group relative text-left bg-white rounded-2xl border transition-all overflow-hidden ${
        isOut
          ? "opacity-60 cursor-not-allowed border-[#E5E7EB]"
          : "border-[#E5E7EB] hover:border-[#121216]/20 hover:-translate-y-0.5 active:scale-[0.98] cursor-pointer"
      }`}
      style={{
        boxShadow: isOut ? "none" : "0 1px 2px rgba(17,24,39,0.04)",
      }}
      onMouseEnter={(e) => {
        if (!isOut) e.currentTarget.style.boxShadow = `0 12px 28px ${palette.spine}26`;
      }}
      onMouseLeave={(e) => {
        if (!isOut) e.currentTarget.style.boxShadow = "0 1px 2px rgba(17,24,39,0.04)";
      }}
    >
      {/* Hover "+ Add" badge — bottom-right, only visible on mouse hover or focus. */}
      {!isOut && (
        <div
          className="absolute right-2.5 top-2.5 w-7 h-7 rounded-full bg-[#121216] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-100 scale-75"
          aria-hidden="true"
        >
          <Plus size={13} weight="bold" />
        </div>
      )}

      {/* Out-of-stock ribbon */}
      {isOut && (
        <div className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#FEE2E2] text-[#DC2626] text-[10px] font-bold tracking-wide uppercase">
          <Prohibit size={10} weight="bold" />
          Sold out
        </div>
      )}

      <div className="px-4 pt-3 pb-3.5">
        {/* Kind + category row */}
        <div className="flex items-center gap-2 mb-2 h-4">
          {kindLabel && (
            <span
              className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.08em]"
              style={{ background: palette.bg, color: palette.fg }}
            >
              {kindLabel}
            </span>
          )}
          {product.category && (
            <span className="text-[10px] text-[#9CA3AF] uppercase tracking-[0.08em] truncate">
              {product.category}
            </span>
          )}
        </div>

        {/* Product name — the primary scan target */}
        <div className="text-[14px] md:text-[15px] font-semibold leading-tight text-[#121216] mb-3 line-clamp-2 min-h-[2.3rem]">
          {product.name}
        </div>

        {/* Price row */}
        <div className="flex items-end justify-between gap-2 mb-2.5">
          <div className="flex items-baseline gap-0.5">
            <span className="text-[12px] font-semibold text-[#9CA3AF] translate-y-[-2px]">$</span>
            <span className={`font-display font-bold text-[22px] md:text-[24px] tracking-tight tabular-nums ${isOut ? "line-through text-[#9CA3AF]" : "text-[#121216]"}`}>
              {money(product.price).replace(/^\$/, "")}
            </span>
          </div>
          <div className="font-mono text-[9px] text-[#9CA3AF] tracking-wider truncate max-w-[50%]">
            {product.sku}
          </div>
        </div>

        {/* Stock indicator — bar + label; colour ramps from red (empty) → green (full) */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${barPct}%`, background: barColour }}
            />
          </div>
          <div
            className="text-[11px] font-semibold tabular-nums whitespace-nowrap"
            style={{ color: labelColour }}
          >
            {isOut ? "0" : product.stock}
          </div>
        </div>
      </div>
    </button>
  );
}

// Memoised — the grid can hit 200+ products and re-renders whenever cart changes,
// so skip children that haven't moved.
const ProductCard = memo(ProductCardInner, (prev, next) => {
  const a = prev.product, b = next.product;
  return (
    a.id === b.id &&
    a.stock === b.stock &&
    a.price === b.price &&
    a.name === b.name &&
    a.category === b.category &&
    prev.onAdd === next.onAdd
  );
});

export default ProductCard;
